import { useState, useEffect, useCallback, useMemo } from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient';
import { useTranslation } from '../i18n';
import { ORDER_STAGE_FIELDS } from '../constants/orderStage';
import { OPEN_VISIT_FIELDS, SESSION_FIELDS } from './useVisitServices';

// The clinic's medical services — the ones it performs IN-HOUSE, by a staff
// member or by a doctor at a time of their own. Declaring a service here is what
// makes an order for it medical service (docs/medical-service-design.md §1): schedulable,
// assignable to a performer, and billable. A service the clinic does not declare
// stays an ordinary order the patient takes elsewhere — which is why labs never
// appear here.

const PERFORMER_FIELDS = `
  type id label isDefault members { type id name active }
`;

const DECLARATION_FIELDS = `
  currency id catalogId clinicId doctorId description unitPrice isActive
  category orderType isCustom catalogRetired durationMinutes performerRole defaultPerformerId
  requiresAppointment prepInstructions
  # What part of the mouth the service treats. Read from the catalogue row, so a
  # standard service answers the same for every clinic; null for everyone but a
  # dental clinic. The dentist app's plan form is the reader.
  defaultApplicability
  # And what it DOES to that tooth — what «ثبت انجام» proposes charting. Same
  # story: read from the catalogue row, null for everyone but a dental clinic.
  defaultChartEffect
  # «قابل ارائه در» — the catalogue row's clinic types. The drawer shows and
  # edits them for a row this clinic invented; null when unreadable.
  clinicTypes
  schedulingMode performers { ${PERFORMER_FIELDS} }
  # «قابل نوبت‌گیری توسط بیمار» — the clinic's intent, stored on the declaration
  # (PM-0464). Whether a request is actually possible also needs a time and a
  # performer; the table says all three (serviceDefaults.bookabilityState).
  patientBookable
  # «نمایش عمومی» (PM-0468 §2.1) — display, where patientBookable is permission.
  publicVisible
  hasConsumables hasCommission
  # How insurance treats the service — part of the definition, so it travels
  # with it rather than being fetched again per drawer.
  insurance {
    currency
    id catalogId itemType coverageMode
    acceptedBasicInsurerIds allowSupplementary acceptedSupplementaryInsurerIds
    calcMode patientSharePct insurerAmountRial insurerCeilingRial
    maxQuantityPerVisit minDaysBetween requiresDoctorOrder conditionsFa
    referralRequiredDirect indirectDocument multiInsurerRule
  }
`;

// Who at this clinic may be named as a performer — what the drawer's chips and
// the bulk grid's batch picker are populated from.
const PERFORMER_CANDIDATES = `
  query PerformerCandidates {
    performerCandidates {
      staff { type id name active }
      doctors { type id name active }
      roles { ${PERFORMER_FIELDS} }
    }
  }
`;

const BOOK_SERVICE_SESSION = `
  mutation BookServiceSession($input: BookServiceSessionInput!) {
    bookServiceSession(input: $input) { id dateTime providerId ticketNo schedulingMode }
  }
`;

const ENQUEUE_SERVICE_TICKET = `
  mutation EnqueueServiceTicket($input: EnqueueServiceTicketInput!) {
    enqueueServiceTicket(input: $input) {
      providerId
      entries { id patientId placeholderLabel patientName: name ticketNo queuePosition status schedulingMode }
    }
  }
`;

// The full cost picture — the stored model joined against LIVE warehousing unit
// costs. Read only when a service is opened: the list needs the two boolean
// indicators above, not a warehousing round-trip per row.
const COST_MODEL_FIELDS = `
  currency declarationId
  consumables { id itemId itemName unit quantity unitCost currentStock lineCostRial }
  sideCosts { id label amountRial }
  commissionRules { id doctorId mode value basis }
  consumableCostRial sideCostTotalRial commissionRial
  totalCostRial marginRial marginPct
`;

const SERVICE_COST_MODEL = `
  query ServiceCostModel($declarationId: ID!) {
    serviceCostModel(declarationId: $declarationId) { ${COST_MODEL_FIELDS} }
  }
`;

// REPLACE-ALL per list: an omitted list is treated as empty, which is how the last
// consumable or the only commission rule is removed.
const SET_COST_MODEL = `
  mutation SetServiceCostModel($declarationId: ID!, $input: ServiceCostModelInput!) {
    setServiceCostModel(declarationId: $declarationId, input: $input) { ${COST_MODEL_FIELDS} }
  }
`;

const MEDICAL_SERVICES = `
  query ClinicServiceDeclarations {
    clinicServiceDeclarations { ${DECLARATION_FIELDS} }
  }
`;

const ORDER_CATALOG = `
  query OrderCatalog($orderType: String, $clinicType: String, $includeOtherTypes: Boolean) {
    orderCatalog(
      orderType: $orderType
      clinicType: $clinicType
      includeOtherTypes: $includeOtherTypes
    ) {
      id orderType name nameEn category code modality bodyPart ownerType
      clinicTypes appliesToClinic declared
      defaultDurationMinutes defaultPerformerRole defaultRequiresAppointment
      defaultPatientBookable
      defaultApplicability defaultChartEffect
      # A BUNDLE's fixed parts (docs/TREATMENT_PROCESS_AND_SERVICE_BUNDLES_PLAN.md
      # D13) — one level, never optional. The drawer shows them, and edits them
      # on a service this clinic invented.
      isBundle components { childId quantity position childName childOrderType }
    }
  }
`;

const SET_SERVICE = `
  mutation SetClinicServiceDeclaration($input: ClinicServiceDeclarationInput!) {
    setClinicServiceDeclaration(input: $input) { ${DECLARATION_FIELDS} }
  }
`;

// A service the clinic invents: creates a clinic-owned catalog row AND its
// declaration in one call, so a custom service travels the same catalog → declaration
// path as a global one — no more free-floating custom-* code.
export const CREATE_CUSTOM_SERVICE = `
  mutation CreateCustomService($input: CustomServiceInput!) {
    createCustomService(input: $input) { ${DECLARATION_FIELDS} }
  }
`;

// Editing a service the clinic invented: its name, its group, and the clinic
// types it may be offered to. A GLOBAL service has no equivalent — the clinic
// re-prices it and re-titles its invoice line through the declaration instead.
const UPDATE_CUSTOM_SERVICE = `
  mutation UpdateCustomService($catalogId: ID!, $input: CustomServiceInput!) {
    updateCustomService(catalogId: $catalogId, input: $input) { ${DECLARATION_FIELDS} }
  }
`;

// The bulk flow: twenty services picked off the standard list and priced in one
// grid become one mutation, one refetch and one toast.
const DECLARE_SERVICES = `
  mutation DeclareClinicServices($input: [ClinicServiceDeclarationInput!]!) {
    declareClinicServices(input: $input) { ${DECLARATION_FIELDS} }
  }
`;

const DELETE_SERVICE = `
  mutation DeleteClinicServiceDeclaration($declarationId: ID!, $catalogId: String) {
    deleteClinicServiceDeclaration(declarationId: $declarationId, catalogId: $catalogId)
  }
`;

const MEDICAL_SERVICE_ORDERS = `
  query MedicalServiceOrders($status: [OrderStatus!]) {
    medicalServiceOrders(status: $status) {
      currency
      id appointmentId patientId patientName patientPhone doctorId
      description catalogId urgency status createdAt notes sessionCount
      unitPrice durationMinutes performerRole requiresAppointment prepInstructions
      schedulingMode declarationId performers { ${PERFORMER_FIELDS} }
      # Where the order stands with the till (billed = on an invoice, paid = nothing
      # owed). invoiceAppointmentId is the appointment of the invoice «فاکتور»
      # opens — where the money is due, which is not always the order's own visit.
      billed paid invoiceId invoiceAppointmentId
      # Its place in a treatment process, when it has one. An order with a
      # processId is shown INSIDE its process on the treatments tab; ready and
      # blockedReasonFa are the service's verdict, never worked out here.
      processId sequenceNo parentOrderId approvedAt
      ready blockedReason blockedReasonFa completedSessions
      # Where it stands (PM-0634): «تاریخ انجام», and the BFF's stage with the
      # soonest booking — what tells the desk it is already booked.
      ${ORDER_STAGE_FIELDS}
      # One appointment, one invoice (PM-0618): every session row with the
      # مراجعه it sits in and its charge there, the next one waiting, the
      # «اقدام‌های درمانی» filter it falls in, and a package prepaid on a bill.
      sessions { ${SESSION_FIELDS} }
      nextSessionNo overdue due prepaidSessions prepaidInvoiceNumber
      # The patient's open مراجعه a session of it can join now (null = unread):
      # where «افزودن به این مراجعه» goes, and — not empty — why a QUEUE turn is
      # refused (ORDER_USE_THIS_VISIT), so «نوبت بده» is not offered.
      attachableVisits { ${OPEN_VISIT_FIELDS} }
      # What «افزودن به فاکتور» may still prepay (R7): the sessions less those on a
      # bill at their own price and the units a package holds. null = unread.
      prepayableSessions
    }
  }
`;

const PATIENT_IN_HOUSE_ORDERS = `
  query PatientInHouseOrders($patientId: ID!) {
    patientInHouseOrders(patientId: $patientId) {
      currency
      id appointmentId patientId patientName patientPhone doctorId
      description catalogId urgency status notes createdAt sessionCount
      unitPrice durationMinutes performerRole requiresAppointment prepInstructions billed
      declarationId prepayableSessions
      ${ORDER_STAGE_FIELDS}
    }
  }
`;

export const PERFORMER_ROLES = (t) => ([
  { value: 'doctor', label: t('common.roles.doctor') },
  { value: 'nurse', label: t('common.roles.nurse') },
  { value: 'physiotherapist', label: t('common.roles.physiotherapist') },
  { value: 'technician', label: t('common.roles.technician') },
  { value: 'radiologist', label: t('common.roles.radiologist') },
]);

// Takes `t` — a plain module function, not a component. Named `t` on purpose:
// docs/I18N.md explains why a forwarder keeps that name.
export const performerLabel = (role, t) =>
  PERFORMER_ROLES(t).find(r => r.value === role)?.label || '—';

export function useMedicalServices() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await bffQuery(MEDICAL_SERVICES);
      setServices(d.clinicServiceDeclarations || []);
      setError(null);
    } catch (e) {
      setError(e.message || t('errors.load.services'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refresh(); }, [refresh]);

  // A catalog-backed service (picked from the dictionary, or an existing clinic-owned
  // one being edited). The upsert is keyed on (catalogId, doctorId), so re-saving an
  // existing service re-prices it rather than duplicating it.
  const saveService = useCallback(async (input) => {
    const saved = await bffMutation(SET_SERVICE, { input });
    await refresh();
    return saved.setClinicServiceDeclaration;
  }, [refresh]);

  // A service the dictionary has no entry for — the BFF creates the clinic-owned
  // catalog entry and its declaration together.
  const createCustom = useCallback(async (input) => {
    const saved = await bffMutation(CREATE_CUSTOM_SERVICE, { input });
    await refresh();
    return saved.createCustomService;
  }, [refresh]);

  // Edit a clinic-invented service. Owner-guarded server-side: a global catalogId
  // comes back as CATALOG_SERVICE_NOT_EDITABLE rather than rewriting the shared
  // dictionary, which is exactly what the drawer's read-only name field prevents
  // anyone from trying in the first place.
  const updateCustom = useCallback(async (catalogId, input) => {
    const saved = await bffMutation(UPDATE_CUSTOM_SERVICE, { catalogId, input });
    await refresh();
    return saved.updateCustomService;
  }, [refresh]);

  // Declare many at once. A PARTIAL failure still throws — the rows that saved
  // are saved, and `err.extensions.failed` names the ones that did not so the
  // grid can keep exactly those and clear the rest.
  const declareMany = useCallback(async (rows) => {
    const saved = await bffMutation(DECLARE_SERVICES, { input: rows });
    await refresh();
    return saved.declareClinicServices || [];
  }, [refresh]);

  // Pass the whole service: a clinic-invented one also retires its catalog entry, which
  // needs the catalogId (a global catalogId is ignored by the BFF).
  const removeService = useCallback(async (service) => {
    await bffMutation(DELETE_SERVICE, {
      declarationId: service.id,
      catalogId: service.catalogId || null,
    });
    await refresh();
  }, [refresh]);

  return {
    services, loading, error, refresh,
    saveService, createCustom, updateCustom, declareMany, removeService,
  };
}

/**
 * One service's cost model, loaded when its drawer opens.
 *
 * Deliberately NOT loaded with the list: composing a margin joins warehousing for
 * live unit costs, and paying for that on every row of a fifty-service table to
 * render two indicator dots would be a bad trade. The list gets
 * hasConsumables/hasCommission; the drawer gets the numbers.
 */
export function useServiceCostModel(declarationId) {
  const { t } = useTranslation();
  const [costModel, setCostModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!declarationId) { setCostModel(null); setLoading(false); return; }
    setLoading(true);
    try {
      const d = await bffQuery(SERVICE_COST_MODEL, { declarationId });
      setCostModel(d.serviceCostModel || null);
      setError(null);
    } catch (e) {
      setError(e.message || t('errors.load.servicePrice'));
      setCostModel(null);
    } finally {
      setLoading(false);
    }
  }, [declarationId, t]);

  useEffect(() => { refresh(); }, [refresh]);

  // Returns the COMPOSED model the server just computed, so the drawer shows the
  // margin its save produced instead of flashing the old number first.
  // `targetId` exists for the one caller the closed-over id cannot serve: a
  // BRAND-NEW service, whose declaration id only comes into being in the save
  // above this one. Without it the mutation goes out with declarationId: null and
  // the server rejects the variable outright.
  const save = useCallback(async ({ consumables, sideCosts, commissionRules }, targetId) => {
    const id = targetId || declarationId;
    if (!id) throw new Error(t('errors.services.notSavedYet'));
    const d = await bffMutation(SET_COST_MODEL, {
      declarationId: id,
      input: {
        consumables: (consumables || []).map((c) => ({
          itemId: c.itemId, itemName: c.itemName, unit: c.unit, quantity: Number(c.quantity) || 0,
        })),
        sideCosts: (sideCosts || []).map((s) => ({
          label: s.label, amountRial: Math.round(Number(s.amountRial) || 0),
        })),
        // `basis` travels both ways: this write is replace-all, so a rule read
        // without it would be saved back as `gross`.
        commissionRules: (commissionRules || []).map((r) => ({
          doctorId: r.doctorId || null, mode: r.mode, value: Number(r.value) || 0,
          basis: r.basis || null,
        })),
      },
    });
    const saved = d.setServiceCostModel || null;
    // Only adopt it as THIS hook's state when it is this hook's service — a
    // new service's model belongs to a drawer that is about to close.
    if (id === declarationId) setCostModel(saved);
    return saved;
  }, [declarationId, t]);

  return { costModel, loading, error, refresh, save };
}

// What the clinic's doctors ordered that the clinic has to perform. Empty until the
// clinic declares at least one service — an order for something the clinic does not
// offer is not medical service, so it never lands here.
export function useMedicalServiceOrders(statuses) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const key = statuses ? statuses.join(',') : '';

  const refresh = useCallback(async () => {
    try {
      const d = await bffQuery(MEDICAL_SERVICE_ORDERS, { status: key ? key.split(',') : null });
      setOrders(d.medicalServiceOrders || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => { refresh(); }, [refresh]);

  return { orders, loading, refresh };
}

// One patient's in-house (paraclinic) orders — the same orders ∩ offerings intersection
// as above, scoped to a single patient instead of the whole clinic. `billed` says a
// medical-service invoice line already exists for the order, so the cashier bills only the
// rest. Empty until the patient has an order for something the clinic performs in-house.
export function usePatientInHouseOrders(patientId) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!patientId) { setOrders([]); setLoading(false); return; }
    try {
      const d = await bffQuery(PATIENT_IN_HOUSE_ORDERS, { patientId });
      setOrders(d.patientInHouseOrders || []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { orders, loading, refresh };
}

// ── Who may perform, and the two ways a service session comes to be ─────────

export function usePerformerCandidates(enabled = true) {
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  useEffect(() => {
    if (!enabled) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    bffQuery(PERFORMER_CANDIDATES, {}, 'PerformerCandidates')
      .then((d) => { if (!cancelled) setCandidates(d?.performerCandidates || { staff: [], doctors: [], roles: [] }); })
      .catch(() => { if (!cancelled) setCandidates({ staff: [], doctors: [], roles: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled]);
  return { candidates, loading };
}

/** A TIMED session: a slot of the declaration's duration on one performer's calendar. */
export async function bookServiceSession({ orderId, patientId, declarationId, performer, dateTimeMs, reminderNote, sessionId }) {
  const d = await bffMutation(BOOK_SERVICE_SESSION, {
    input: {
      orderId, patientId, declarationId, performer, dateTime: String(dateTimeMs),
      reminderNote: reminderNote || undefined,
      // «جلسهٔ بعدی» (PM-0618): the session row the booking is attached to;
      // omitted, the BFF attaches the next waiting one.
      sessionId: sessionId || undefined,
    },
  });
  return d?.bookServiceSession;
}

/** A QUEUE turn: the patient is here. Returns the performer's queue, ticket included. */
export async function enqueueServiceTicket({ orderId, patientId, declarationId, performer }) {
  const d = await bffMutation(ENQUEUE_SERVICE_TICKET, {
    input: { orderId, patientId, declarationId, performer },
  });
  return d?.enqueueServiceTicket;
}

/**
 * The dictionary the clinic picks from when declaring what it performs in-house.
 *
 * ── What `clinicType` does, and what it deliberately does not ────────────────
 *
 * Passing the clinic's own type narrows the list to the services THAT KIND of
 * clinic is offered, plus the ones offered to «همه انواع». It is a SUGGESTION
 * FILTER, never a permission: `includeOtherTypes` brings the rest back, marked
 * with `appliesToClinic: false`, and every one of them is still declarable. A
 * general clinic with a physiotherapy room may say so — the clinic's declaration
 * is its own statement about what it does, and the type is only a hint about
 * where to start.
 *
 * Passing NO type is not the same as passing a wrong one: it means "do not filter
 * by type at all", which is what an unfiltered picker wants and what a clinic
 * whose type could not be read gets.
 *
 * `orderType` defaults to null — every section — because the standard list a
 * clinic starts from is not only procedures any more: `treatment` is where dental
 * and aesthetic work lives, and a dental clinic filtered to `procedure` would be
 * shown an empty list.
 */
export function useOrderCatalog({
  orderType = null, clinicType = null, includeOtherTypes = false, enabled = true,
} = {}) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(!!enabled);
  const [error, setError] = useState(null);
  // Bumped to force a refetch after a declaration lands, so `declared` — which
  // the BFF joins on per request — stops being stale the moment it matters.
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) { setEntries([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    bffQuery(ORDER_CATALOG, {
      orderType: orderType || null,
      clinicType: clinicType || null,
      includeOtherTypes: !!includeOtherTypes,
    })
      .then((d) => { if (!cancelled) { setEntries(d.orderCatalog || []); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        setEntries([]);
        // Surfaced, not swallowed. An empty picker and an unreachable picker look
        // identical, and the second one is not the clinic's fault to fix.
        setError(e.message || t('errors.operations.catalogUnavailable'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderType, clinicType, includeOtherTypes, enabled, nonce, t]);

  /**
   * `[[groupLabel, entries], …]`, applicable groups first.
   *
   * Rows for other clinic types are grouped SEPARATELY — «سایر انواع · ارتوپدی» —
   * rather than mixed into the same category heading. Two services under one
   * heading where one of them is greyed out reads as a rendering bug; two
   * headings reads as two lists, which is what it is.
   */
  const byCategory = useMemo(() => {
    const groups = new Map();
    for (const e of entries) {
      const category = e.category || t('common.state.other');
      const key = e.appliesToClinic === false
        ? t('operations.catalog.otherTypesGroup', { group: category })
        : category;
      if (!groups.has(key)) groups.set(key, { applies: e.appliesToClinic !== false, rows: [] });
      groups.get(key).rows.push(e);
    }
    return [...groups.entries()]
      .sort((a, b) => Number(b[1].applies) - Number(a[1].applies))
      .map(([label, group]) => [label, group.rows]);
  }, [entries, t]);

  return { entries, byCategory, loading, error, reload };
}
